import { Link, useParams } from "react-router-dom";
import { beautyArticles } from "@/mocks/beauty-info";
import ArticleBody from "./components/ArticleBody";
import RelatedArticles from "./components/RelatedArticles";
import ShareButtons from "./components/ShareButtons";
import ArticleNav from "./components/ArticleNav";
import { scrollToTop } from "@/components/feature/ScrollToTop";

export default function BeautyInfoDetail() {
  const { id } = useParams<{ id: string }>();
  const article = beautyArticles.find((item) => item.id === id);

  if (!article) {
    return (
      <section className="w-full px-4 md:px-6 pt-28 md:pt-36 pb-20 md:pb-28">
        <div className="max-w-3xl mx-auto flex flex-col items-center text-center gap-5">
          <span className="w-16 h-16 rounded-full bg-primary-50 flex items-center justify-center">
            <i className="ri-file-unknow-line text-3xl text-primary-500"></i>
          </span>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-900">
            글을 찾을 수 없습니다
          </h1>
          <p className="text-sm md:text-base text-foreground-600">
            요청하신 글은 삭제되었거나 잘못된 주소입니다.
          </p>
          <Link
            to="/beauty-info"
            onClick={scrollToTop}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-all cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            뷰티정보 목록으로
          </Link>
        </div>
      </section>
    );
  }

  const related = beautyArticles
    .filter((item) => item.id !== article.id)
    .sort((a, b) => {
      const aMatch = a.category === article.category ? 0 : 1;
      const bMatch = b.category === article.category ? 0 : 1;
      return aMatch - bMatch;
    })
    .slice(0, 3);

  const currentIndex = beautyArticles.findIndex((item) => item.id === article.id);
  const prevArticle = currentIndex > 0 ? beautyArticles[currentIndex - 1] : null;
  const nextArticle =
    currentIndex < beautyArticles.length - 1 ? beautyArticles[currentIndex + 1] : null;

  return (
    <>
      {/* 글 헤더 */}
      <section className="w-full px-4 md:px-6 pt-28 md:pt-36 pb-10 md:pb-14">
        <div className="max-w-3xl mx-auto flex flex-col gap-6">
          <button
            type="button"
            onClick={() => {
              window.REACT_APP_NAVIGATE("/beauty-info");
              scrollToTop();
            }}
            className="inline-flex items-center gap-2 text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer self-start"
          >
            <i className="ri-arrow-left-line"></i>
            뷰티정보 목록
          </button>

          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
              <span className="px-3 py-1 rounded-full bg-primary-50 text-primary-600 font-medium">
                {article.category}
              </span>
              <span className="inline-flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="inline-flex items-center gap-1">
                <i className="ri-time-line"></i>
                {article.readTime}
              </span>
            </div>
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 leading-tight">
              {article.title}
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
              {article.excerpt}
            </p>
          </div>
        </div>
      </section>

      {/* 대표 이미지 */}
      <section className="w-full px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="relative w-full h-60 md:h-96 rounded-3xl overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              title={`${article.title} - 비앙카 뷰티정보`}
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* 본문 */}
      <section className="w-full px-4 md:px-6 py-12 md:py-16">
        <div className="max-w-3xl mx-auto">
          <ArticleBody sections={article.content} />

          {/* 태그 */}
          <div className="mt-12 md:mt-16 flex flex-wrap items-center gap-2 pt-8 border-t border-background-200/70">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1.5 rounded-full bg-secondary-100/70 text-secondary-900 text-xs font-medium"
              >
                #{tag}
              </span>
            ))}
          </div>

          {/* 공유하기 */}
          <div className="mt-8 flex flex-col gap-4">
            <span className="text-sm font-semibold text-foreground-900">이 글 공유하기</span>
            <ShareButtons title={article.title} />
          </div>
        </div>
      </section>

      {/* 관련 글 */}
      <section className="w-full px-4 md:px-6 pb-16 md:pb-24">
        <div className="max-w-6xl mx-auto flex flex-col gap-8">
          <div className="flex flex-col gap-2">
            <span className="inline-flex items-center gap-3 text-xs font-medium tracking-[0.2em] text-primary-600 uppercase">
              <span className="w-6 h-px bg-primary-400"></span>
              Related
              <span className="w-6 h-px bg-primary-400"></span>
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900">
              함께 보면 좋은 글
            </h2>
          </div>
          <RelatedArticles articles={related} />
        </div>
      </section>

      {/* 예약 CTA */}
      <section className="w-full px-4 md:px-6 pb-16 md:pb-24">
        <div className="max-w-6xl mx-auto">
          <div className="relative overflow-hidden rounded-3xl bg-primary-500 px-6 md:px-12 py-12 md:py-16 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex flex-col gap-3 text-center md:text-left">
              <h2 className="text-2xl md:text-3xl font-bold text-white leading-tight">
                피부 고민, 전문가와 상담해보세요
              </h2>
              <p className="text-sm md:text-base text-white/85 leading-relaxed">
                비앙카에서 당신의 피부에 맞는 관리를 제안해드립니다.
              </p>
            </div>
            <Link
              to="/reservation"
              onClick={scrollToTop}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-primary-600 text-sm font-semibold whitespace-nowrap hover:bg-primary-50 transition-all cursor-pointer"
            >
              예약 상담하기
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* 이전/다음 글 */}
      <section className="w-full px-4 md:px-6 pb-4">
        <div className="max-w-4xl mx-auto">
          <ArticleNav prev={prevArticle} next={nextArticle} />
        </div>
      </section>
    </>
  );
}